import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, AtSign, Mail, Send, CheckCircle2, XCircle } from "lucide-react";

const FREE_DOMAINS = ["gmail.com", "hotmail.com", "outlook.com", "yahoo.com", "live.com"];

const METODO_ENVIO = {
  pendiente: "Pendiente (borrador, no envía)",
  resend: "Resend (dominio propio)",
  smtp: "SMTP de la casilla",
};
const METODO_BADGE = {
  pendiente: "bg-slate-100 text-slate-600 border-slate-200",
  resend: "bg-violet-50 text-violet-700 border-violet-200",
  smtp: "bg-sky-50 text-sky-700 border-sky-200",
};

const VACIO = {
  nombre: "", casilla_respuesta: "", remitente_nombre: "", emails: "", cuenta_labels: "",
  metodo_envio: "pendiente", resend_from: "", resend_api_key_env: "",
  smtp_host: "", smtp_port: 587, smtp_user: "", smtp_secret_env: "", activo: true,
};

function MarcaDialog({ open, onOpenChange, marca, onSaved }) {
  const isEdit = !!marca;
  const [form, setForm] = useState(VACIO);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (open) {
      setForm(
        marca
          ? {
              nombre: marca.nombre || "",
              casilla_respuesta: marca.casilla_respuesta || "",
              remitente_nombre: marca.remitente_nombre || "",
              emails: (marca.emails || []).join(", "),
              cuenta_labels: (marca.cuenta_labels || []).join(", "),
              metodo_envio: marca.metodo_envio || "pendiente",
              resend_from: marca.resend_from || "",
              resend_api_key_env: marca.resend_api_key_env || "",
              smtp_host: marca.smtp_host || "",
              smtp_port: marca.smtp_port || 587,
              smtp_user: marca.smtp_user || "",
              smtp_secret_env: marca.smtp_secret_env || "",
              activo: marca.activo !== false,
            }
          : VACIO
      );
    }
  }, [open, marca]);

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Indicá el nombre de la marca.");
    if (!form.casilla_respuesta.trim()) return toast.error("Indicá la casilla de respuesta (From).");
    setSaving(true);
    const lista = (s) => s.split(/[,\n;]/).map((r) => r.trim()).filter(Boolean);
    const payload = {
      nombre: form.nombre.trim(),
      casilla_respuesta: form.casilla_respuesta.trim(),
      remitente_nombre: form.remitente_nombre.trim(),
      emails: lista(form.emails),
      cuenta_labels: lista(form.cuenta_labels),
      metodo_envio: form.metodo_envio,
      resend_from: form.resend_from.trim(),
      resend_api_key_env: form.resend_api_key_env.trim(),
      smtp_host: form.smtp_host.trim(),
      smtp_port: Number(form.smtp_port) || 587,
      smtp_user: form.smtp_user.trim(),
      smtp_secret_env: form.smtp_secret_env.trim(),
      activo: form.activo,
    };
    try {
      if (isEdit) {
        await api.put(`/marcas/${marca.id}`, payload);
        toast.success("Marca actualizada.");
      } else {
        await api.post("/marcas", payload);
        toast.success("Marca creada.");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="marca-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AtSign className="h-5 w-5" /> {isEdit ? "Editar marca" : "Nueva marca"}
          </DialogTitle>
          <DialogDescription>
            La respuesta automática al cliente sale DESDE la casilla de la marca a la que escribió.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre de la marca</Label>
              <Input value={form.nombre} onChange={(e) => set({ nombre: e.target.value })}
                placeholder="Ej. Vacaciones" data-testid="marca-nombre-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre del remitente (From)</Label>
              <Input value={form.remitente_nombre} onChange={(e) => set({ remitente_nombre: e.target.value })}
                placeholder="Ej. Vacaciones Nueva York" data-testid="marca-remitente-nombre-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Casilla de respuesta (From)</Label>
            <Input value={form.casilla_respuesta} onChange={(e) => set({ casilla_respuesta: e.target.value })}
              placeholder="vacacionesnuevayork@gmail.com" data-testid="marca-casilla-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Emails de detección (To / Delivered-To, separados por coma)</Label>
            <Textarea value={form.emails} onChange={(e) => set({ emails: e.target.value })} rows={2}
              placeholder="vacacionesnuevayork@gmail.com" data-testid="marca-emails-input" />
            <p className="text-[11px] text-slate-400">Si el correo reenviado iba dirigido a alguno de estos, se asigna a esta marca.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Etiquetas de cuenta (GYG / Bokun, separadas por coma)</Label>
            <Textarea value={form.cuenta_labels} onChange={(e) => set({ cuenta_labels: e.target.value })} rows={2}
              placeholder="Get Vacaciones, Vacaciones" data-testid="marca-cuenta-labels-input" />
          </div>

          <div className="rounded-md border border-slate-200 p-3 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Método de envío</Label>
              <Select value={form.metodo_envio} onValueChange={(v) => set({ metodo_envio: v })}>
                <SelectTrigger data-testid="marca-metodo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(METODO_ENVIO).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">
                Las credenciales nunca se guardan acá: se cargan por variable de entorno. Hasta entonces, la
                respuesta queda como borrador.
              </p>
            </div>
            {form.metodo_envio === "resend" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">From verificado en Resend</Label>
                  <Input value={form.resend_from} onChange={(e) => set({ resend_from: e.target.value })}
                    placeholder="reservas@vacaciones.com" data-testid="marca-resend-from-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Variable de entorno (API key)</Label>
                  <Input value={form.resend_api_key_env} onChange={(e) => set({ resend_api_key_env: e.target.value })}
                    placeholder="RESEND_API_KEY_VACACIONES" data-testid="marca-resend-env-input" />
                </div>
                {FREE_DOMAINS.some((d) => form.casilla_respuesta.toLowerCase().endsWith(`@${d}`)) && (
                  <p className="sm:col-span-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="marca-resend-dominio-gratuito-warning">
                    Resend solo puede mandar desde un dominio VERIFICADO propio. Esta casilla es de dominio gratuito
                    ({form.casilla_respuesta.split("@")[1]}) y NO va a funcionar con Resend — usá "SMTP de la casilla"
                    con la contraseña de aplicación de esa cuenta.
                  </p>
                )}
              </div>
            )}
            {form.metodo_envio === "smtp" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Host SMTP</Label>
                  <Input value={form.smtp_host} onChange={(e) => set({ smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com" data-testid="marca-smtp-host-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Puerto</Label>
                  <Input type="number" value={form.smtp_port} onChange={(e) => set({ smtp_port: e.target.value })}
                    data-testid="marca-smtp-port-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Usuario</Label>
                  <Input value={form.smtp_user} onChange={(e) => set({ smtp_user: e.target.value })}
                    placeholder="casilla@dominio.com" data-testid="marca-smtp-user-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Variable de entorno (contraseña)</Label>
                  <Input value={form.smtp_secret_env} onChange={(e) => set({ smtp_secret_env: e.target.value })}
                    placeholder="SMTP_PWD_VACACIONES" data-testid="marca-smtp-env-input" />
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
            <div>
              <p className="text-sm font-medium text-slate-800">Activa</p>
              <p className="text-[11px] text-slate-400">Las inactivas no se usan para detectar ni responder.</p>
            </div>
            <Switch checked={form.activo} onCheckedChange={(v) => set({ activo: v })} data-testid="marca-activo-switch" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="marca-cancel">Cancelar</Button>
          <Button onClick={guardar} disabled={saving} data-testid="marca-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Guardar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProbarEnvioDialog({ open, onOpenChange, marca }) {
  const [destinatario, setDestinatario] = useState("");
  const [sending, setSending] = useState(false);
  const [resultado, setResultado] = useState(null);

  useEffect(() => {
    if (open) { setDestinatario(""); setResultado(null); }
  }, [open]);

  const probar = async () => {
    if (!destinatario.trim()) return toast.error("Indicá un correo del equipo para recibir la prueba.");
    setSending(true);
    setResultado(null);
    try {
      const { data } = await api.post(`/marcas/${marca.id}/probar-envio`, { destinatario: destinatario.trim() });
      setResultado(data);
      if (data.enviado) toast.success("Correo de prueba enviado.");
      else toast.info(`No se envió: ${data.motivo || "transporte no conectado"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!marca) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="probar-envio-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Send className="h-5 w-5" /> Probar envío</DialogTitle>
          <DialogDescription>
            Manda un correo de prueba desde la casilla de {marca.nombre} ({marca.casilla_respuesta}) a una dirección
            del equipo, sin escribirle a ningún cliente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs">Destinatario (del equipo)</Label>
          <Input value={destinatario} onChange={(e) => setDestinatario(e.target.value)}
            placeholder="tu-correo@equipo.com" data-testid="probar-envio-destinatario-input" />
        </div>
        {resultado && (
          <p className={`text-[12px] rounded px-2 py-1.5 border ${resultado.enviado ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`} data-testid="probar-envio-resultado">
            {resultado.enviado ? "Enviado correctamente." : `No se envió (${resultado.motivo || "transporte no conectado"}).`}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="probar-envio-cerrar">Cerrar</Button>
          <Button onClick={probar} disabled={sending} data-testid="probar-envio-enviar-btn" className="gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Enviar prueba
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarcasConfig() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [probando, setProbando] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marcas");
      setAll(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (m, valor) => {
    setAll((prev) => prev.map((x) => (x.id === m.id ? { ...x, activo: valor } : x)));
    try {
      await api.put(`/marcas/${m.id}`, { activo: valor });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setAll((prev) => prev.map((x) => (x.id === m.id ? { ...x, activo: !valor } : x)));
    }
  };

  const abrirNueva = () => { setEditing(null); setDialogOpen(true); };
  const abrirEdit = (m) => { setEditing(m); setDialogOpen(true); };

  return (
    <div className="space-y-4" data-testid="marcas-config">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <AtSign className="h-5 w-5" /> Marcas / Envío
          </h3>
          <p className="text-sm text-slate-500 mt-0.5 max-w-2xl">
            Cada reserva queda asociada a su marca. Las respuestas automáticas salen DESDE la casilla de la
            marca a la que el cliente escribió. El envío real se activa al conectar el transporte por cuenta.
          </p>
        </div>
        <Button onClick={abrirNueva} data-testid="nueva-marca-btn" className="gap-2">
          <Plus className="h-4 w-4" /> Nueva marca
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Marca", "Casilla de respuesta (From)", "Detección", "Método de envío", "Conectado", "Reservas", "Activa", ""].map((h, i) => (
                  <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="h-32 text-center">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400 inline" />
                </TableCell></TableRow>
              ) : all.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="h-32 text-center text-slate-400 text-sm">
                  No hay marcas configuradas.
                </TableCell></TableRow>
              ) : (
                all.map((m) => (
                  <TableRow key={m.id} data-testid={`marca-row-${m.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-slate-900">
                      {m.nombre}
                      {m.es_central && (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-amber-50 text-amber-700 border-amber-200">central</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[13px] text-slate-700">
                      <span className="inline-flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 text-slate-400" />
                        {m.casilla_respuesta}
                      </span>
                    </TableCell>
                    <TableCell className="text-[11px] text-slate-500 max-w-[260px]">
                      <span className="block truncate">{(m.emails || []).join(", ") || "—"}</span>
                      <span className="block truncate text-slate-400">{(m.cuenta_labels || []).join(", ")}</span>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${METODO_BADGE[m.metodo_envio] || METODO_BADGE.pendiente}`}>
                        {METODO_ENVIO[m.metodo_envio]?.split(" (")[0] || m.metodo_envio}
                      </span>
                    </TableCell>
                    <TableCell>
                      {m.envio_conectado ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700" data-testid={`marca-conectado-${m.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5" /> Conectada
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400" data-testid={`marca-conectado-${m.id}`}>
                          <XCircle className="h-3.5 w-3.5" /> Pendiente
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{m.reservas_count ?? 0}</TableCell>
                    <TableCell>
                      <Switch checked={m.activo !== false} onCheckedChange={(v) => toggle(m, v)} data-testid={`marca-activo-${m.id}`} />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setProbando(m)} data-testid={`probar-envio-btn-${m.id}`}
                        className="h-8 text-slate-500 hover:text-slate-900 gap-1.5">
                        <Send className="h-3.5 w-3.5" /> Probar envío
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => abrirEdit(m)} data-testid={`edit-marca-${m.id}`}
                        className="h-8 w-8 text-slate-500 hover:text-slate-900">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <MarcaDialog open={dialogOpen} onOpenChange={setDialogOpen} marca={editing} onSaved={load} />
      <ProbarEnvioDialog open={!!probando} onOpenChange={(v) => !v && setProbando(null)} marca={probando} />
    </div>
  );
}
